import { PageBreadCrumbs, TreeToolbar } from "slot-definitions"
import { Box, Stack, Typography } from "@mui/material"
import { PageContainer } from "lib/@components/page-container"
import { TopBox } from "lib/@components/topBox"
import { ToolbarBox } from "lib/@components/toolbarBox"
import { StandardBreadcrumbs } from "lib/@components/standardBreadcrumbs"
import { ListItemBox } from "lib/@components/ListItemBox"
import { BackButton } from "routes/schedule/tree/schedules/upDirectory"
import { ensureArray } from "lib/ensure-array"
import { forwardRef, isValidElement } from "react"
import { If } from "./@components/switch"
import { BreadcrumbCrumb } from "./breadcrumb-crumb"

export const PageBreadcrumbContainer = forwardRef(function PageBreadcrumbContainer(
    {
        children,
        parentCrumbs = [],
        title,
        heading = title,
        crumb = title,
        backButton = <BackButton />,
        downloadButton,
        hideCrumbs = false,
        description,
        descriptionProps,
        ...props
    },
    ref
) {
    function normaliseCrumb(c) {
        if (isValidElement(c)) return { element: c }
        if (typeof c === "string") return { label: c }
        if (c && typeof c === "object" && ("label" in c || "to" in c || "href" in c)) return c
        return { label: String(c) }
    }

    parentCrumbs = ensureArray(parentCrumbs).map(normaliseCrumb)

    const crumbs = PageBreadCrumbs.useSlot(
        <If truthy={!hideCrumbs}>
            <Box
                sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    maxWidth: "15vw",
                    whiteSpace: "nowrap",
                    display: "block",
                }}
            />
        </If>
    )

    return (
        <PageContainer ref={ref} title={title} {...props} data-cy="page-breadcrumb-container">
            <Box mx={0} width={1} height={1} display="flex" flexDirection="column">
                <TopBox>
                    <ToolbarBox>
                        <Stack spacing={1}>
                            <Typography variant="h4" component="div">
                                {heading}
                            </Typography>
                            {description && (
                                <Typography variant="body2" component="div" {...descriptionProps}>
                                    {description}
                                </Typography>
                            )}
                            <If truthy={!hideCrumbs}>
                                <StandardBreadcrumbs
                                    sx={{
                                        mx: 0,
                                        "& li:last-child": {
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    {parentCrumbs.map((crumb, idx) => (
                                        <BreadcrumbCrumb crumb={crumb} key={idx} />
                                    ))}
                                    <Box
                                        sx={{
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            maxWidth: "15vw",
                                            whiteSpace: "nowrap",
                                            display: "block",
                                        }}
                                    >
                                        {crumb}
                                    </Box>
                                    {crumbs}
                                </StandardBreadcrumbs>
                            </If>
                            <ListItemBox mb={1} pt={1} data-cy="page-breadcrumb-toolbar">
                                {backButton}
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    whiteSpace="nowrap"
                                    flex={1}
                                    justifyContent="flex-end"
                                    display="flex"
                                    alignItems="center"
                                    width="max-content"
                                >
                                    {downloadButton}
                                    <TreeToolbar.Slot />
                                </Stack>
                            </ListItemBox>
                        </Stack>
                    </ToolbarBox>
                </TopBox>
                {children}
            </Box>
        </PageContainer>
    )
})
